import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  XMarkIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  IdentificationIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { 
  getCompetencyDefinition, 
  createCompetencyDefinition, 
  updateCompetencyDefinition 
} from "../../../../api/hse/competency";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Definition Edit - Create or edit competency definition
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyDefinitionEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    type: "", // CV, certificate, course, license, other
    requires_renewal: false,
    renewal_interval_days: null,
    is_active: true,
  });

  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: isNew ? "create" : "edit" });

  useEffect(() => {
    if (!isNew) {
      fetchDefinition();
    }
  }, [id]);

  const fetchDefinition = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getCompetencyDefinition(id);
      setFormData({
        name: response.competency_definition?.name || "",
        description: response.competency_definition?.description || "",
        category: response.competency_definition?.category || "",
        type: response.competency_definition?.type || "",
        requires_renewal: response.competency_definition?.requires_renewal || false,
        renewal_interval_days: response.competency_definition?.renewal_interval_days || null,
        is_active: response.competency_definition?.is_active !== undefined ? response.competency_definition.is_active : true,
      });
    } catch (err) {
      console.error("Error fetching definition:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kompetansedefinisjon"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t("Navn er påkrevd"));
      return;
    }

    if (!formData.type) {
      toast.error(t("Type er påkrevd"));
      return;
    }

    try {
      setSaving(true);
      
      // Convert type to competency_type for API
      const apiData = {
        ...formData,
        competency_type: formData.type,
      };
      delete apiData.type; // Remove type field
      
      let response;
      if (isNew) {
        response = await createCompetencyDefinition(apiData);
        toast.success(t("Kompetanse opprettet"));
        navigate(`/dashboard/hse-ks/competency/definitions/${response.competency_definition?.id || response.id}`);
      } else {
        response = await updateCompetencyDefinition(id, apiData);
        toast.success(t("Kompetanse oppdatert"));
        navigate(`/dashboard/hse-ks/competency/definitions/${id}`);
      }
    } catch (err) {
      console.error("Error saving definition:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Kunne ikke lagre kompetanse");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { value: "", label: t("Velg kategori") },
    { value: "safety", label: t("Sikkerhet") },
    { value: "quality", label: t("Kvalitet") },
    { value: "environment", label: t("Miljø") },
    { value: "technical", label: t("Teknisk") },
    { value: "management", label: t("Ledelse") },
    { value: "other", label: t("Annet") },
  ];

  const types = [
    { value: "", label: t("Velg type") },
    { value: "cv", label: t("CV") },
    { value: "certificate", label: t("Sertifikat") },
    { value: "course", label: t("Kurs") },
    { value: "license", label: t("Lisens") },
    { value: "other", label: t("Annet") },
  ];

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency/definitions");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kompetanse")]} />;
  }

  return (
    <HsePageChrome
      title={isNew ? t("Ny kompetanse") : t("Rediger kompetanse")}
      subtitle={t("Kompetanseskjema")}
      backTo="/dashboard/hse-ks/competency/definitions"
      backButton={{
        label: isNew ? t("Tilbake til definisjoner") : t("Tilbake til definisjoner"),
        action: () => navigate("/dashboard/hse-ks/competency/definitions"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <CompetencySubnav />
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Grunnleggende informasjon")}</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t("Navn")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("F.eks. Førstehjelp, Sikkerhetskurs, etc.")}
                  />
                </div>

                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    {t("Kategori")}
                  </label>
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                    {t("Type")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    id="type"
                    required
                    value={formData.type}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    {types.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Renewal Interval */}
                <div>
                  <label htmlFor="renewal_interval_days" className="block text-sm font-medium text-gray-700">
                    {t("Fornyelsesintervall (dager)")}
                  </label>
                  <input
                    type="number"
                    name="renewal_interval_days"
                    id="renewal_interval_days"
                    min="0"
                    value={formData.renewal_interval_days || ""}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("La stå tomt for permanent")}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("Antall dager før kompetansen må fornyes. La stå tomt for kompetanse som ikke utløper")}
                  </p>
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t("Beskrivelse")}
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Beskriv hva denne kompetansen innebærer...")}
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Options */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Innstillinger")}</h3>
              
              <div className="space-y-4">
                {/* Requires Renewal */}
                <div className="flex items-center">
                  <input
                    id="requires_renewal"
                    name="requires_renewal"
                    type="checkbox"
                    checked={formData.requires_renewal}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="requires_renewal" className="ml-2 block text-sm text-gray-900">
                    {t("Krever fornyelse")}
                  </label>
                </div>

                {/* Is Active */}
                <div className="flex items-center">
                  <input
                    id="is_active"
                    name="is_active"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    {t("Aktiv")}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/hse-ks/competency/definitions")}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? t("Lagrer...") : isNew ? t("Opprett") : t("Lagre endringer")}
            </button>
          </div>
        </form>
      </div>
    </HsePageChrome>
  );
};

